import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TextField, Button } from '@mui/material';
import * as Styled from './IntegrationsPage.styles';
import { getFromLocalStorage } from '../../global/helpers';
import { UserRole } from '../OrdersPage/types';
import { selectors } from './reducer';
import * as actions from './actions';
import IntegrationsTable from './components/IntegrationsTable/IntegrationsTable';
import NetworksTable from './components/NetworksTable/NetworksTable';
import CreateIntegrationDialog from './components/CreateIntegrationDialog/CreateIntegrationDialog';
import AddNetworkDialog from './components/AddNetworkDialog/AddNetworkDialog';
import ConfirmRemoveDialog from './components/ConfirmRemoveDialog/ConfirmRemoveDialog';
import EditIntegrationDialog from './components/EditIntegrationDialog/EditIntegrationDialog';

const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const role = getFromLocalStorage('role');

  const integrations = useSelector(selectors.integrationsList);
  const integrationsLoading = useSelector(selectors.integrationsListLoading);
  const selectedIntegration = useSelector(selectors.selectedIntegration);
  const selectedIntegrationLoading = useSelector(
    selectors.selectedIntegrationLoading
  );
  const mutationLoading = useSelector(selectors.integrationsMutationLoading);

  const [filterCode, setFilterCode] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [networkCodeToRemove, setNetworkCodeToRemove] = useState<string | null>(
    null
  );

  // Загружаем список интеграций при монтировании
  useEffect(() => {
    dispatch(actions.getIntegrations.request());
  }, [dispatch]);

  const filteredIntegrations = useMemo(
    () =>
      (integrations || []).filter((item) =>
        item.code.toLowerCase().includes(filterCode.toLowerCase())
      ),
    [integrations, filterCode]
  );

  const handleSelectIntegration = (code: string) => {
    dispatch(actions.getIntegrationByCode.request({ code }));
  };

  const handleCreateIntegration = (code: string, networkCodes: string[]) => {
    dispatch(
      actions.createIntegration.request({
        code,
        network_codes: networkCodes.length ? networkCodes : undefined,
        postEffect: () => setCreateOpen(false),
      })
    );
  };

  const handleAddNetworkClick = () => {
    setChangeDialogOpen(true);
  };

  const handleRemoveNetworkClick = (networkCode: string) => {
    setNetworkCodeToRemove(networkCode);
    setConfirmRemoveOpen(true);
  };

  const handleConfirmRemoveNetwork = () => {
    if (!networkCodeToRemove || !selectedIntegration) return;
    dispatch(
      actions.removeNetworkFromIntegration.request({
        integration_code: selectedIntegration.code,
        network_code: networkCodeToRemove,
        postEffect: () => {
          setConfirmRemoveOpen(false);
          setNetworkCodeToRemove(null);
        },
      })
    );
  };

  const handleToggleNetworkActive = (networkCode: string, active: boolean) => {
    if (!selectedIntegration) return;
    dispatch(
      actions.setNetworkActive.request({
        integration_code: selectedIntegration.code,
        network_code: networkCode,
        active,
      })
    );
  };

  const handleChangeNetworkSubmit = (networkCode: string) => {
    if (!selectedIntegration) return;
    dispatch(
      actions.addNetworkToIntegration.request({
        integration_code: selectedIntegration.code,
        network_code: networkCode,
        postEffect: () => setChangeDialogOpen(false),
      })
    );
  };

  const handleEditInfoSubmit = (
    frontUrl: string,
    senderName: string,
    smsKey: string
  ) => {
    if (!selectedIntegration) return;
    const payload: {
      integration_code: string;
      front_url?: string;
      sender?: { name: string; sms_key: string };
      postEffect?: () => void;
    } = {
      integration_code: selectedIntegration.code,
      postEffect: () => setEditInfoOpen(false),
    };
    if (frontUrl) payload.front_url = frontUrl;
    if (senderName || smsKey) {
      payload.sender = { name: senderName, sms_key: smsKey };
    }
    dispatch(actions.updateIntegrationInfo.request(payload));
  };

  // Страница и действие доступны только админу
  if (role && role !== UserRole.ADMIN) {
    return null;
  }

  return (
    <Styled.IntegrationsPageContainer>
      <Styled.HeaderRow>
        <div className="title">{t('IntegrationsPage.TITLE')}</div>
        <div className="actions">
          <TextField
            size="small"
            label={t('IntegrationsPage.Filters.CODE')}
            value={filterCode}
            onChange={(e) => setFilterCode(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => setCreateOpen(true)}
            disabled={mutationLoading}
          >
            {t('IntegrationsPage.Actions.CREATE')}
          </Button>
        </div>
      </Styled.HeaderRow>

      <Styled.ContentLayout>
        <Styled.Card>
          <Styled.CardTitle>
            {t('IntegrationsPage.Blocks.INTEGRATIONS_LIST')}
          </Styled.CardTitle>
          <Styled.InfoText>
            {t('IntegrationsPage.Blocks.INTEGRATIONS_LIST_HINT')}
          </Styled.InfoText>
          <IntegrationsTable
            data={filteredIntegrations}
            loading={integrationsLoading}
            selectedCode={selectedIntegration?.code || null}
            onSelect={handleSelectIntegration}
          />
        </Styled.Card>

        <Styled.Card>
          <Styled.CardTitle>
            {t('IntegrationsPage.Blocks.NETWORKS')}
          </Styled.CardTitle>
          <Styled.InfoText>
            {t('IntegrationsPage.Blocks.NETWORKS_HINT')}
          </Styled.InfoText>
          {selectedIntegrationLoading && !selectedIntegration && (
            <div>{t('IntegrationsPage.Common.LOADING')}</div>
          )}
          {!selectedIntegration && !selectedIntegrationLoading && (
            <div>{t('IntegrationsPage.Blocks.NETWORKS_EMPTY')}</div>
          )}
          {selectedIntegration && (
            <NetworksTable
              integration={selectedIntegration}
              mutationLoading={mutationLoading}
              onAddNetworkClick={handleAddNetworkClick}
              onRemoveNetworkClick={handleRemoveNetworkClick}
              onToggleActiveClick={handleToggleNetworkActive}
              onEditInfoClick={() => setEditInfoOpen(true)}
            />
          )}
        </Styled.Card>
      </Styled.ContentLayout>

      {/* Диалоги */}
      <CreateIntegrationDialog
        open={createOpen}
        loading={mutationLoading}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateIntegration}
      />

      {selectedIntegration && (
        <AddNetworkDialog
          open={changeDialogOpen}
          integrationCode={selectedIntegration.code}
          loading={mutationLoading}
          onClose={() => setChangeDialogOpen(false)}
          onSubmit={handleChangeNetworkSubmit}
        />
      )}

      {/* Подтверждение удаления сети */}
      {selectedIntegration && (
        <ConfirmRemoveDialog
          open={confirmRemoveOpen}
          loading={mutationLoading}
          networkCode={networkCodeToRemove}
          onClose={() => {
            setConfirmRemoveOpen(false);
            setNetworkCodeToRemove(null);
          }}
          onConfirm={handleConfirmRemoveNetwork}
        />
      )}

      {/* Редактирование информации об интеграции */}
      {selectedIntegration && (
        <EditIntegrationDialog
          open={editInfoOpen}
          loading={mutationLoading}
          integration={selectedIntegration}
          onClose={() => setEditInfoOpen(false)}
          onSubmit={handleEditInfoSubmit}
        />
      )}
    </Styled.IntegrationsPageContainer>
  );
};

export default IntegrationsPage;
